import type { Money, ReasonCode, TradingAccount } from '../../../api';
import type { ApiQueryResult } from '../../../lib/api/use-api-query';
import type { ScreenFailure } from '../channel-selection/screen-failure';

/** The only platform E4 offers and sends (S6 R2, ADR 0008); the contract still names MT4. */
type Platform = 'MT5';

const PLATFORM: Platform = 'MT5';

/**
 * What the trader has typed on E4: the platform is not a field, it is always MetaTrader 5 (S6 R2,
 * ADR 0008). It holds the password, so it exists only inside `AccountForm` and for
 * the one call of `connect`: no view, no hook result and no state outside the form keeps it (I9, R4).
 */
export interface AccountDraft {
  readonly accountNumber: string;
  readonly password: string;
  readonly server: string;
}

/** A draft the contract accepts: the body of `POST /trading-account`. */
export interface ConnectDraft {
  readonly platform: Platform;
  readonly accountNumber: string;
  readonly password: string;
  readonly server: string;
}

/**
 * What of a draft may outlive a submission: everything but the password (S6 R28, AC4: a refused
 * submit keeps the number and the server).
 */
export type KeptFields = Pick<AccountDraft, 'accountNumber' | 'server'>;

/** Longest password and server the contract takes (`ConnectTradingAccountRequestSchema`). */
export const MAX_FIELD_LENGTH = 128;
/** Longest account number the contract takes. */
export const MAX_ACCOUNT_NUMBER_LENGTH = 20;

const ACCOUNT_NUMBER = /^\d{1,20}$/;

/**
 * The draft as the contract takes it, or `null` while a field is missing or out of bounds
 * (S6 R2): the platform is always MT5, the number is 1 to 20 digits, the password and the server are 1
 * to 128 characters. "Check the account" is enabled exactly when this is not `null`.
 */
export function toConnectDraft(draft: AccountDraft): ConnectDraft | null {
  const { accountNumber, password, server } = draft;
  if (!ACCOUNT_NUMBER.test(accountNumber)) return null;
  if (password.length < 1 || password.length > MAX_FIELD_LENGTH) return null;
  if (server.length < 1 || server.length > MAX_FIELD_LENGTH) return null;
  return { platform: PLATFORM, accountNumber, password, server };
}

/** Whether "Check the account" may be pressed (S6 R2). */
export function isFormValid(draft: AccountDraft): boolean {
  return toConnectDraft(draft) !== null;
}

/**
 * The account as E4 shows it: the contract summary, the last 4 digits only (I9, R18, Q81). The
 * balance is `null` only when an unreachable account's balance is unknown (R16).
 */
export interface AccountSummaryView {
  /** What the API reports; the contract still names MT4 (ADR 0008), E4 only ever sends MT5. */
  readonly platform: 'MT4' | 'MT5';
  readonly server: string;
  readonly last4: string;
  readonly holderName: string;
  readonly currency: string;
  readonly balance: Money | null;
  readonly isDemo: boolean;
  readonly warnings: readonly ReasonCode[];
}

/**
 * What E4 renders: exactly one view per state of spec §4. No view carries the password or a full
 * account number (I9): the form keeps what the trader typed, the views only what the server says.
 */
export type TradingAccountView =
  | { readonly kind: 'loading' }
  | { readonly kind: 'readFailed'; readonly failure: ScreenFailure }
  | {
      readonly kind: 'form';
      /** The refusal to show: a failed submit first, else the rejection of the last verification. */
      readonly failure: ScreenFailure | null;
      /** True for `TRADING_ACCOUNT_READ_ONLY` (R9): adds the title and "Where to find it?". */
      readonly readOnly: boolean;
      /** True when the server rejected the last attempt: the button reads "Check again". */
      readonly rejected: boolean;
    }
  | { readonly kind: 'verifying' }
  | {
      readonly kind: 'found';
      readonly summary: AccountSummaryView;
      readonly connectFailure: ScreenFailure | null;
      /** Changes with every answer of the server: a new verification remounts the consent (R13, I7). */
      readonly linkKey: number;
    }
  | { readonly kind: 'connected'; readonly summary: AccountSummaryView }
  | {
      readonly kind: 'unreachable';
      readonly summary: AccountSummaryView;
      readonly failure: ScreenFailure;
      readonly connectFailure: ScreenFailure | null;
    };

/** What {@link toTradingAccountView} takes of the writes (`useTradingAccountWrites`). */
export interface ConnectWrites {
  /** A `POST /trading-account` is in flight. */
  readonly connecting: boolean;
  /** The refusal of the last submit, or `null` (R28). */
  readonly connectFailure: ScreenFailure | null;
  /** The server's 202 answer, kept until a newer read replaces it (a `VERIFYING`), or `null`. */
  readonly overlay: TradingAccount | null;
}

type Summarised = Extract<TradingAccount, { status: 'VERIFIED' | 'OPERATIONAL' | 'UNREACHABLE' }>;

function summaryOf(account: Summarised): AccountSummaryView {
  return {
    platform: account.platform,
    server: account.server,
    last4: account.accountLast4,
    holderName: account.holderName,
    currency: account.currency,
    balance: account.balance,
    isDemo: account.isDemo,
    warnings: account.warnings,
  };
}

/**
 * The view of `GET /trading-account` with the answers of the writes (specs/S6-trading-account
 * R5, R6, R9, R16, R28, R30). Pure: the route feeds it the read and the writes; it never sees a
 * draft.
 */
export function toTradingAccountView(
  query: ApiQueryResult<TradingAccount>,
  writes: ConnectWrites,
): TradingAccountView {
  if (query.status === 'pending') return { kind: 'loading' };
  if (query.status === 'error') {
    return {
      kind: 'readFailed',
      failure: { reasonCode: query.error.reasonCode, params: query.error.params },
    };
  }
  if (writes.connecting) return { kind: 'verifying' };
  const { connectFailure } = writes;
  const account = writes.overlay ?? query.data;
  switch (account.status) {
    case 'NONE':
      return { kind: 'form', failure: connectFailure, readOnly: false, rejected: false };
    case 'VERIFYING':
      return { kind: 'verifying' };
    case 'REJECTED':
      return {
        kind: 'form',
        failure: connectFailure ?? { reasonCode: account.reasonCode },
        readOnly: connectFailure === null && account.reasonCode === 'TRADING_ACCOUNT_READ_ONLY',
        rejected: true,
      };
    case 'VERIFIED':
      return {
        kind: 'found',
        summary: summaryOf(account),
        connectFailure,
        linkKey: query.receivedAt,
      };
    case 'OPERATIONAL':
      return { kind: 'connected', summary: summaryOf(account) };
    case 'UNREACHABLE':
      return {
        kind: 'unreachable',
        summary: summaryOf(account),
        failure: { reasonCode: account.reasonCode },
        connectFailure,
      };
  }
}
